/* ==========================================================================
   DavyBarkmo · Landingpage SoulBowl (templates/product.soulbowl-lp.json)
   Paketwahl „Komplettset" / „SoulBowl" ohne Neuladen.

   Ablauf beim Wechsel:
   1. Passende Variante (gleiche Farbe und Größe) im anderen Produkt aus den Daten der Karten suchen
   2. Produktbereich des anderen Produkts per Section Rendering holen und austauschen
      (Focal-Komponenten starten dabei von selbst, weil sie Custom Elements sind)
   3. Fremd-Apps anstoßen, die keine Custom Elements sind (siehe appsAnstossen)
   4. URL per history.replaceState mitziehen
   Schlägt etwas fehl, wird ganz normal zur Seite des anderen Produkts navigiert.
   ========================================================================== */

(() => {
  const PAKETE = '[data-db-pakete]';
  let laufenderAbruf = null;

  const sektionVon = (element) => element.closest('.shopify-section');

  const datenVon = (pakete) => {
    try {
      return JSON.parse(pakete.querySelector('[data-db-pakete-daten]').textContent);
    } catch (fehler) {
      return null;
    }
  };

  const aktuelleVariantenId = (sektion) => {
    const feld = sektion.querySelector('form[is="product-form"] input[name="id"]');
    return feld ? String(feld.value) : null;
  };

  const kombiZurVariante = (daten, variantenId) => {
    for (const [titel, kombi] of Object.entries(daten.kombis)) {
      if (String(kombi.bowl.id) === variantenId || String(kombi.set.id) === variantenId) {
        return { titel, kombi };
      }
    }
    return null;
  };

  const zielUrl = (daten, ziel, variantenId) => {
    const url = new URL(daten[ziel].url, window.location.origin);
    if (daten[ziel].view) url.searchParams.set('view', daten[ziel].view);
    url.searchParams.set('variant', variantenId);
    return url;
  };

  /* ---------- Karten an Farbe und Größe anpassen, ohne Tausch ---------- */

  const feld = (pakete, name) => pakete.querySelector(`[data-db-feld="${name}"]`);

  const kartenAktualisieren = (pakete, variantenId) => {
    const daten = datenVon(pakete);
    const treffer = daten && kombiZurVariante(daten, variantenId);
    if (!treffer) return;

    const { kombi } = treffer;
    const setzen = (name, text) => {
      const element = feld(pakete, name);
      if (!element) return;
      if (text === null || text === undefined) {
        element.hidden = true;
      } else {
        element.textContent = text;
        element.hidden = false;
      }
    };

    setzen('set-preis', kombi.set.preis);
    setzen('streich', kombi.set.streich);
    setzen('prozent', kombi.set.prozent > 0 ? `−${kombi.set.prozent} %` : null);
    setzen('ersparnis', kombi.set.ersparnis);
    setzen('bowl-preis', kombi.bowl.preis);
    setzen('bowl-streich', kombi.bowl.streich);

    const ersparnisZeile = feld(pakete, 'ersparnis-zeile');
    if (ersparnisZeile) ersparnisZeile.hidden = !kombi.set.ersparnis;
    const setVersand = feld(pakete, 'set-versand');
    if (setVersand) setVersand.hidden = !kombi.set.versandfrei;
    const bowlVersand = feld(pakete, 'bowl-versand');
    if (bowlVersand) bowlVersand.hidden = kombi.bowl.versandfrei;

    pakete.querySelectorAll('[data-db-bild]').forEach((bild) => {
      const quelle = kombi.bilder[Number(bild.dataset.dbBild)];
      if (quelle && bild.getAttribute('src') !== quelle) bild.src = quelle;
    });

    ['set', 'bowl'].forEach((ziel) => {
      const url = zielUrl(daten, ziel, kombi[ziel].id);
      pakete.querySelector(`[data-db-paket="${ziel}"]`).href = url.pathname + url.search;
    });
  };

  /* ---------- Der Wechsel ---------- */

  const wechseln = async (karte) => {
    const pakete = karte.closest(PAKETE);
    const sektion = sektionVon(pakete);
    const daten = datenVon(pakete);
    const ziel = karte.dataset.dbPaket;
    const treffer = daten && kombiZurVariante(daten, aktuelleVariantenId(sektion));

    if (!treffer) {
      window.location.href = karte.href;
      return;
    }

    const neueUrl = zielUrl(daten, ziel, treffer.kombi[ziel].id);
    const abrufUrl = new URL(neueUrl);
    abrufUrl.searchParams.set('section_id', sektion.id.replace(/^shopify-section-/, ''));

    if (laufenderAbruf) laufenderAbruf.abort();
    laufenderAbruf = new AbortController();
    pakete.setAttribute('aria-busy', 'true');

    try {
      const antwort = await fetch(abrufUrl, { signal: laufenderAbruf.signal });
      if (!antwort.ok) throw new Error(`Section Rendering: ${antwort.status}`);

      const html = await antwort.text();
      const neu = new DOMParser().parseFromString(html, 'text/html').getElementById(sektion.id);
      if (!neu || !neu.querySelector(PAKETE)) throw new Error('Antwort ohne Paketwahl');

      const abstandVorher = pakete.getBoundingClientRect().top;
      sektion.innerHTML = neu.innerHTML;

      const neuePakete = sektion.querySelector(PAKETE);
      window.scrollBy(0, neuePakete.getBoundingClientRect().top - abstandVorher);
      window.history.replaceState(window.history.state, '', neueUrl.pathname + neueUrl.search + window.location.hash);
      const vorher = ziel === 'set' ? daten.bowl.titel : daten.set.titel;
      if (vorher && document.title.includes(vorher)) document.title = document.title.replace(vorher, daten[ziel].titel);

      appsAnstossen(sektion);
      ankerHalten(sektion, neuePakete);

      const aktiv = neuePakete.querySelector(`[data-db-paket="${ziel}"]`);
      if (aktiv) aktiv.focus({ preventScroll: true });

      document.dispatchEvent(new CustomEvent('db:paket-gewechselt', {
        detail: { ziel, variantenId: treffer.kombi[ziel].id, sektion },
      }));
    } catch (fehler) {
      if (fehler.name === 'AbortError') return;
      console.warn('[DavyBarkmo] Paketwechsel ohne Neuladen fehlgeschlagen, lade Seite neu.', fehler);
      window.location.href = neueUrl.pathname + neueUrl.search;
    }
  };

  /* ---------- Sprünge nach dem Tausch abfangen ---------- */

  // Nach dem Tausch ändert der Bereich seine Höhe noch einige Sekunden lang: Bilder laden,
  // der Variant Image Automator ersetzt die Galerie. Chrome hält den Scroll dabei von selbst
  // an Ort und Stelle, Safari nicht. Deshalb bleibt die Paketwahl so lange an derselben
  // Stelle im Fenster, wie der Bereich noch wächst oder schrumpft.
  const ankerHalten = (sektion, anker, dauer = 5000) => {
    if (typeof ResizeObserver !== 'function') return;
    let letzterTop = anker.getBoundingClientRect().top;
    const merken = () => { letzterTop = anker.getBoundingClientRect().top; };
    const beobachter = new ResizeObserver(() => {
      if (!anker.isConnected) { aufhoeren(); return; }
      const delta = anker.getBoundingClientRect().top - letzterTop;
      if (Math.abs(delta) > 1) window.scrollBy(0, delta);
      merken();
    });
    const aufhoeren = () => {
      beobachter.disconnect();
      window.removeEventListener('scroll', merken);
    };
    beobachter.observe(sektion);
    window.addEventListener('scroll', merken, { passive: true });
    setTimeout(aufhoeren, dauer);
  };

  // Beim Farb- oder Größenwechsel baut der Variant Image Automator die Galerie neu und leert
  // dabei kurz die Vorschauleiste. Liegt das große Bild schon über dem Fenster, bleibt der
  // Infobereich darunter an Ort und Stelle. Ist das Bild noch zu sehen, passiert die Änderung
  // im Blickfeld und braucht keinen Ausgleich.
  const galerieAnkern = (sektion) => {
    const sichtbar = (element) => element.getClientRects().length > 0;
    const bild = [...sektion.querySelectorAll('.product__media-list-wrapper')].find(sichtbar);
    const info = sektion.querySelector('.product__info');
    if (!bild || !info || bild.getBoundingClientRect().bottom > 0) return;
    ankerHalten(sektion, info, 2500);
  };

  /* ---------- Fremd-Apps im ausgetauschten Bereich ---------- */

  const appsAnstossen = (sektion) => {
    // Variant Image Automator (StarApps): liest beim Start Produkt-URL und Galerie ein und legt dafür
    // versteckte Hilfselemente an. Die alten gehören zum vorherigen Produkt und müssen weg, sonst
    // schreibt die App in das erste (alte) Element. Danach neu starten; die URL ist schon umgestellt.
    // Bis die App fertig ist (beim ersten Wechsel ca. 2 s), zeigt Focal schon das richtige Variantenbild.
    if (typeof window.variantImageAutomator === 'function') {
      document.querySelectorAll('#multi-variant-info, #multi-variant-thumb, #multi-variant-dots').forEach((alt) => alt.remove());
      try {
        window.variantImageAutomator();
      } catch (fehler) {
        console.warn('[DavyBarkmo] Variant Image Automator ließ sich nicht neu starten.', fehler);
      }
    }

    // Judge.me: Im Landingpage-Kopf stehen die Sterne serverseitig. Falls später ein Judge.me-Badge
    // in den Produktbereich kommt, wird es hier neu gezeichnet.
    if (sektion.querySelector('.jdgm-prev-badge') && window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
      window.jdgm.customizeBadges();
    }
  };

  /* ---------- Info-Popup (Spende) und Lieferzeit ---------- */

  const infoUmschalten = (knopf, offen) => {
    const text = document.getElementById(knopf.getAttribute('aria-controls'));
    knopf.setAttribute('aria-expanded', String(offen));
    if (text) text.hidden = !offen;
  };

  // Blendet „Lieferung in 1–3 Werktagen" aus, wenn die Variante als verzögerter Versand markiert ist.
  // Die Liste liefert der Variantenwähler von Focal/ed mit (data-delivery-map).
  const lieferzeitAktualisieren = (sektion, variantenId) => {
    const lieferzeit = sektion.querySelector('[data-db-lieferzeit]');
    const karte = sektion.querySelector('[data-delivery-map]');
    if (!lieferzeit || !karte) return;
    try {
      const wert = JSON.parse(karte.textContent)[variantenId];
      lieferzeit.hidden = wert === true || wert === 'true';
    } catch (fehler) {
      lieferzeit.hidden = false;
    }
  };

  /* ---------- Ereignisse ---------- */

  /* ---------- Bewertungen: Seitenwechsel ohne Sprung ---------- */
  /* Beim Blättern leert Judge.me die Liste, bevor die neue Seite da ist: gemessen am 18.09.2026
     fiel das Widget von 1634 auf etwa 450 px und wuchs dann wieder. Alles darunter rutschte
     hoch und zurück, während Judge.me gleichzeitig zum Anfang der Liste scrollt — das ist der
     Ruck. Hier wird nur die Höhe des Widgets gehalten, bis der Austausch durch ist.
     Der Scroll von Judge.me bleibt unangetastet: Er zielt auf den Listenanfang und ist gewollt.
     Ein Versuch, ihn per scrollTo zu überstimmen, ließ die Seite eine halbe Sekunde lang
     zwischen zwei Positionen flackern, weil Judge.me `scrollTop` auf jedem Frame neu setzt.
     Beobachtet wird das Widget selbst: Judge.me ersetzt den Inhalt von `.jdgm-rev-widg__body`
     samt Liste, ein Beobachter auf der Liste hinge danach an einem abgetrennten Knoten.
     Die erste Änderung kommt nach etwa 17 ms (Ladezustand), der Austausch nach etwa 550 ms —
     deshalb wird gewartet, bis 400 ms Ruhe ist, mindestens 900 ms, höchstens 4 Sekunden.
     Gehört wird in der Aufnahmephase, damit die Höhe vor dem Leeren gemessen wird. */
  const BEWERTUNGEN = '.jdgm-rev-widg';

  const bewertungenHoeheHalten = (widget) => {
    widget.style.minHeight = `${Math.round(widget.getBoundingClientRect().height)}px`;
    const start = performance.now();
    let letzteAenderung = start;
    const beobachter = new MutationObserver(() => { letzteAenderung = performance.now(); });
    beobachter.observe(widget, { childList: true, subtree: true });
    const pruefen = () => {
      const jetzt = performance.now();
      const ruhig = jetzt - letzteAenderung > 400 && jetzt - start > 900;
      if (ruhig || jetzt - start > 4000) {
        beobachter.disconnect();
        widget.style.minHeight = '';
        return;
      }
      setTimeout(pruefen, 100);
    };
    setTimeout(pruefen, 100);
  };

  document.addEventListener('click', (event) => {
    const blaettern = event.target.closest(`${BEWERTUNGEN} .jdgm-paginate__page`);
    if (blaettern) bewertungenHoeheHalten(blaettern.closest(BEWERTUNGEN));
  }, true);

  document.addEventListener('click', (event) => {
    const karte = event.target.closest(`${PAKETE} [data-db-paket]`);
    if (karte) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
      event.preventDefault();
      if (karte.getAttribute('aria-checked') !== 'true') wechseln(karte);
      return;
    }

    const infoKnopf = event.target.closest('[data-db-info]');
    document.querySelectorAll('[data-db-info][aria-expanded="true"]').forEach((offen) => {
      if (offen !== infoKnopf) infoUmschalten(offen, false);
    });
    if (infoKnopf) {
      infoUmschalten(infoKnopf, infoKnopf.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const bewertungen = event.target.closest('[data-db-lp-bewertungen]');
    if (bewertungen) {
      const widget = document.querySelector('.jdgm-review-widget, .jdgm-widget.jdgm-review-widget');
      if (widget) {
        event.preventDefault();
        widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    // Kaufbutton-Animation aus ed-atc-button-text-animation-main-product bindet per onclick.
    // Nach einem Tausch sind das neue Buttons, deshalb hier zusätzlich per Delegation.
    if (event.target.closest('.ed-product #AddToCart, #StickyAddToCart')) {
      try {
        edMainAddToCartButtonClicked = true; // eslint-disable-line no-undef
      } catch (fehler) {
        /* Animation nicht auf dieser Seite */
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-db-info][aria-expanded="true"]').forEach((offen) => infoUmschalten(offen, false));
      return;
    }
    if (event.key !== ' ') return;
    const karte = event.target.closest && event.target.closest(`${PAKETE} [data-db-paket]`);
    if (!karte) return;
    event.preventDefault();
    karte.click();
  });

  document.addEventListener('variant:changed', (event) => {
    const sektion = event.target.closest && sektionVon(event.target);
    const pakete = sektion && sektion.querySelector(PAKETE);
    if (!pakete || !event.detail.variant) return;
    galerieAnkern(sektion);
    kartenAktualisieren(pakete, String(event.detail.variant.id));
    lieferzeitAktualisieren(sektion, String(event.detail.variant.id));
  });
})();

/* ==========================================================================
   DavyBarkmo · Landingpages Bauch-Balance
   (templates/product.bauch-balance-lp.json und product.bauch-balance-lp2.json)
   Dosierungsrechner aus snippets/db-lp-rechner.liquid.

   Rechnet aus dem Gewicht die Tagesmenge in Dosierlöffeln und wie viele Wochen die gewählte
   Menge reicht, und zeigt beides im Kalenderlook (eine Ziffer je Kästchen). Die Rechenwerte
   stehen als data-Attribute am Rechner und kommen aus der Vorlage; hier steht keine Zahl.
   Der Rechner fasst weder das Kaufformular noch das Recharge-Widget an. Er meldet jede Änderung
   als Ereignis „db:rechner“, darauf hört die Vorratswahl von Entwurf 2.
   ========================================================================== */

(() => {
  const RECHNER = '[data-db-rechner]';

  const wert = (rechner, name) => Number(rechner.dataset[name]);

  const dosenJeVariante = (rechner) => {
    try {
      return JSON.parse(rechner.querySelector('[data-db-rechner-dosen]').textContent);
    } catch (fehler) {
      return {};
    }
  };

  // Eine Ziffer je Kästchen, mindestens zwei Stellen („04“), bei Bedarf drei („107“)
  const ziffernSetzen = (ziel, zahl) => {
    if (!ziel) return;
    const text = String(zahl).padStart(2, '0');
    ziel.setAttribute('aria-label', String(zahl));
    ziel.replaceChildren(...[...text].map((zeichen) => {
      const kasten = document.createElement('span');
      kasten.className = 'db-rechner__ziffer';
      kasten.setAttribute('aria-hidden', 'true');
      kasten.textContent = zeichen;
      return kasten;
    }));
  };

  const aktualisieren = (rechner) => {
    const schieber = rechner.querySelector('[data-db-rechner-schieber]');
    if (!schieber) return;

    const kg = Number(schieber.value);
    const ungedeckelt = Math.max(1, Math.round(kg * wert(rechner, 'gpk')));
    const gramm = Math.min(wert(rechner, 'max'), ungedeckelt);
    const loeffel = Math.max(1, Math.round(gramm / wert(rechner, 'gpl')));

    rechner.dataset.gramm = gramm;
    rechner.querySelector('[data-db-rechner-kg]').textContent = kg;
    ziffernSetzen(rechner.querySelector('[data-db-rechner-loeffel]'), loeffel);
    const grenze = rechner.querySelector('[data-db-rechner-grenze]');
    if (grenze) grenze.hidden = ungedeckelt < wert(rechner, 'max');

    const spanne = wert(rechner, 'kgMax') - wert(rechner, 'kgMin');
    schieber.style.setProperty('--db-rechner-stand', `${((kg - wert(rechner, 'kgMin')) / spanne) * 100}%`);

    // Reichweite der gewählten Menge: Inhalt der Dosen geteilt durch die Tagesmenge, in Wochen
    const sektion = rechner.closest('.shopify-section');
    const gewaehlt = sektion && sektion.querySelector('.product-form__variants input[name^="option"]:checked');
    const dosen = dosenJeVariante(rechner);
    const anzahl = (gewaehlt && dosen[gewaehlt.value]) || 1;
    const wochen = Math.max(1, Math.round((wert(rechner, 'dose') * anzahl) / gramm / 7));
    ziffernSetzen(rechner.querySelector('[data-db-rechner-wochen]'), wochen);
    const wort = rechner.querySelector('[data-db-rechner-wochen-wort]');
    if (wort) wort.textContent = wochen === 1 ? 'Woche' : 'Wochen';
    const titel = rechner.querySelector('[data-db-rechner-reicht-titel]');
    if (titel && gewaehlt) titel.textContent = `${gewaehlt.value} ${anzahl === 1 ? 'reicht' : 'reichen'} ca.`;

    rechner.dispatchEvent(new CustomEvent('db:rechner', { bubbles: true, detail: { kg, gramm } }));
  };

  const alleAktualisieren = () => document.querySelectorAll(RECHNER).forEach(aktualisieren);

  document.addEventListener('input', (event) => {
    const schieber = event.target.closest && event.target.closest('[data-db-rechner-schieber]');
    if (schieber) aktualisieren(schieber.closest(RECHNER));
  });

  document.addEventListener('click', (event) => {
    const knopf = event.target.closest('[data-db-rechner-schritt]');
    if (!knopf) return;
    const rechner = knopf.closest(RECHNER);
    const schieber = rechner.querySelector('[data-db-rechner-schieber]');
    const neu = Number(schieber.value) + Number(knopf.dataset.dbRechnerSchritt);
    schieber.value = Math.max(wert(rechner, 'kgMin'), Math.min(wert(rechner, 'kgMax'), neu));
    aktualisieren(rechner);
  });

  // Dosenwechsel: andere Reichweite
  document.addEventListener('variant:changed', alleAktualisieren);
  document.addEventListener('shopify:section:load', alleAktualisieren);

  alleAktualisieren();
})();

/* ==========================================================================
   DavyBarkmo · Landingpage Bauch-Balance, Entwurf 1 (templates/product.bauch-balance-lp.json)
   Kaufart am Formular vermerken, damit die Rabatt-Pillen der Dosen-Kacheln nur im Abo stehen.

   Das Recharge-Widget liegt im Shadow DOM und meldet nichts nach außen. Ablesbar ist seine Wahl am
   Feld selling_plan im Kaufformular: leer heißt Einmal-Lieferung. Klicks aus dem Widget kommen am
   document an (click ist „composed“), change nicht — deshalb wird nach jedem Klick ins Widget und
   nach jedem Variantenwechsel nachgelesen. Solange das Widget noch lädt, gilt das Abo: Es ist
   vorgewählt, und die Pillen sollen beim Laden nicht aufblitzen.
   ========================================================================== */

(() => {
  const kaufartLesen = () => {
    document.querySelectorAll('[data-db-abo]').forEach((marker) => {
      const sektion = marker.closest('.shopify-section');
      const formular = sektion && sektion.querySelector('.product-form');
      if (!formular) return;
      const feld = sektion.querySelector('form[is="product-form"] [name="selling_plan"]');
      formular.dataset.dbKauf = feld && feld.value === '' ? 'einmal' : 'abo';
    });
  };

  const gleichLesen = () => { setTimeout(kaufartLesen, 60); setTimeout(kaufartLesen, 400); };

  document.addEventListener('click', (event) => {
    if (event.target.closest && event.target.closest('recharge-subscription-widget')) gleichLesen();
  });
  document.addEventListener('keyup', (event) => {
    if (event.target.closest && event.target.closest('recharge-subscription-widget')) gleichLesen();
  });
  document.addEventListener('variant:changed', gleichLesen);
})();

/* ==========================================================================
   DavyBarkmo · Landingpage Bauch-Balance, Entwurf 2 (templates/product.bauch-balance-lp2.json)
   Vorratswahl aus snippets/db-lp-vorrat.liquid, Vorbild wuffes.com.

   Jede Karte ist eine Variante. Vorrat und Lieferintervall sind dieselbe Zahl: Die Reichweite einer
   Dose (Inhalt ÷ Tagesmenge aus dem Rechner) wird auf ein Vielfaches von data-runden-auf Tagen
   abgerundet, zwei und drei Dosen sind das Doppelte und Dreifache. Abgerundet heißt: Die Lieferung
   kommt immer, bevor es leer ist.
   Zu jeder Karte wird der Abo-Plan mit genau diesen Tagen gesucht. Fehlt er, nimmt die Karte den
   längsten vorhandenen Plan, der nicht länger ist, und zeigt dessen Tage; gibt es keinen, der
   rechtzeitig käme, verschwindet sie. Die Seite verkauft nie ein anderes Intervall, als sie anzeigt.
   Ausnahme nur mit data-vorschau und nur auf einem unveröffentlichten Theme (zum Beurteilen des
   Entwurfs): Dann stehen die gewünschten Tage da; welcher Plan fehlt und was stattdessen im Warenkorb
   landet, steht in der Konsole und auf Wunsch in einem Kasten. Auf dem Live-Theme gilt immer streng.
   Das Script schreibt das Feld selling_plan ins Kaufformular — dasselbe Feld mit denselben
   Plan-Nummern, das sonst das Recharge-Widget schreibt. Diese Vorlage hat kein Widget.
   Die Variante wechselt über Focals eigene (hier unsichtbare) Variantenwahl.
   Einmalkauf: nur die kleinste Variante, direkt in den Warenkorb, ohne Plan.
   ========================================================================== */

(() => {
  const VORRAT = '[data-db-vorrat]';
  const gemeldet = new Set();

  const sektionVon = (element) => element.closest('.shopify-section');
  const warte = (ms) => new Promise((fertig) => setTimeout(fertig, ms));

  const datenVon = (vorrat) => {
    try {
      return JSON.parse(vorrat.querySelector('[data-db-vorrat-daten]').textContent);
    } catch (fehler) {
      return null;
    }
  };

  // Geldformat des Shops (shop.money_format), z. B. „€{{amount_with_comma_separator}}“
  const geld = (vorrat, cent) => {
    const betrag = (cent / 100).toFixed(2);
    const format = vorrat.dataset.geld || '€{{amount_with_comma_separator}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (ganz, art) => (art.includes('comma') ? betrag.replace('.', ',') : betrag));
  };

  const tageText = (tage) => `${tage} ${tage === 1 ? 'Tag' : 'Tage'}`;

  // Ein Monat zählt hier 30 Tage. 45 → „1,5 Monate“, 30 → „1 Monat“
  const MONAT = 30;
  const monateText = (tage) => {
    const monate = tage / MONAT;
    const zahl = Number.isInteger(monate) ? String(monate) : monate.toFixed(1).replace('.', ',');
    return `${zahl} ${monate === 1 ? 'Monat' : 'Monate'}`;
  };

  // Der Plan mit genau diesen Tagen; sonst der längste, der nicht länger ist (kommt rechtzeitig);
  // sonst der kürzeste überhaupt (kommt zu spät — nur für die Vorschau von Belang)
  const planFuer = (variante, tage) => {
    const plaene = [...variante.plaene].sort((x, y) => x.tage - y.tage);
    const genau = plaene.find((plan) => plan.tage === tage);
    if (genau) return { plan: genau, genau: true, rechtzeitig: true };
    const frueher = plaene.filter((plan) => plan.tage < tage).pop();
    if (frueher) return { plan: frueher, genau: false, rechtzeitig: true };
    return { plan: plaene[0] || null, genau: false, rechtzeitig: false };
  };

  const formularVon = (sektion) => sektion.querySelector('form[is="product-form"]');

  const planFeld = (sektion) => {
    const formular = formularVon(sektion);
    if (!formular) return null;
    let feld = formular.querySelector('input[name="selling_plan"]');
    if (!feld) {
      feld = document.createElement('input');
      feld.type = 'hidden';
      feld.name = 'selling_plan';
      formular.appendChild(feld);
    }
    return feld;
  };

  const aktualisieren = (vorrat) => {
    const sektion = sektionVon(vorrat);
    const daten = datenVon(vorrat);
    const rechner = sektion && sektion.querySelector('[data-db-rechner]');
    const formular = sektion && formularVon(sektion);
    if (!daten || !rechner || !formular) return;

    const gramm = Number(rechner.dataset.gramm);
    const doseGramm = Number(rechner.dataset.dose);
    const schritt = Math.max(1, Number(vorrat.dataset.rundenAuf) || 1);
    const tageMax = Number(vorrat.dataset.tageMax) || Infinity;
    // Wunschtage ohne passenden Plan gibt es nur auf einem unveröffentlichten Theme. Auf dem Live-Theme
    // gilt immer der strenge Modus, auch wenn die Vorlage noch vorschau: true trägt.
    const live = Boolean(window.Shopify && window.Shopify.theme && window.Shopify.theme.role === 'main');
    const vorschau = vorrat.hasAttribute('data-vorschau') && !live;
    // Reichweite einer Dose: auf `schritt` Tage abgerundet. Ab `monateAb` Tagen stattdessen auf
    // Monate (Bruchteil `monatSchritt`) abgerundet, und die ganze Reihe zeigt Monate statt Tage.
    const tageJeDose = doseGramm / gramm;
    const monateAb = Number(vorrat.dataset.monateAb) || Infinity;
    const monatSchritt = Number(vorrat.dataset.monatSchritt) || 1;
    // Im Bereich in_tagen_kg („28-32“) bleibt die Reihe in Tagen, auch über monateAb
    const schieber = sektion.querySelector('[data-db-rechner-schieber]');
    const kg = schieber ? Number(schieber.value) : NaN;
    const [kgVon, kgBis] = (vorrat.dataset.inTagenKg || '').split('-').map((teil) => Number(teil.trim()));
    const inTagen = Boolean(vorrat.dataset.inTagenKg) && kg >= kgVon && kg <= kgBis;
    const inMonaten = !inTagen && Math.floor(tageJeDose / schritt) * schritt >= monateAb
      && Math.floor(tageJeDose / (MONAT * monatSchritt)) >= 1;
    const basis = inMonaten
      ? Math.floor(tageJeDose / (MONAT * monatSchritt)) * MONAT * monatSchritt
      : Math.max(1, Math.floor(tageJeDose / schritt) * schritt);
    const dauerText = (tage) => (inMonaten ? monateText(tage) : tageText(tage));
    const wort = vorrat.dataset.wort || 'Vorrat';
    // Zusätzlicher Nachlass auf die erste Abo-Lieferung (erst_rabatt), nur Anzeige
    const erstRabatt = Math.min(90, Math.max(0, Number(vorrat.dataset.erstRabatt) || 0));
    const gewaehlteId = String(formular.querySelector('input[name="id"]').value);
    let gewaehlt = null;
    let ersteSichtbare = null;

    daten.varianten.forEach((variante) => {
      const karte = vorrat.querySelector(`[data-db-vorrat-karte="${variante.id}"]`);
      if (!karte) return;
      const feld = (name) => karte.querySelector(`[data-db-vorrat-feld="${name}"]`);

      const wunsch = basis * variante.dosen;
      const treffer = planFuer(variante, wunsch);
      // Was auf der Karte steht: die gewünschten Tage, wenn es den Plan gibt (oder in der Vorschau),
      // sonst die Tage des Plans, der tatsächlich im Warenkorb landet
      const tage = treffer.genau || vorschau || !treffer.plan ? wunsch : treffer.plan.tage;
      const zeigen = wunsch <= tageMax && Boolean(treffer.plan) && (vorschau || treffer.rechtzeitig);
      karte.hidden = !zeigen;
      if (!zeigen) return;
      if (!ersteSichtbare) ersteSichtbare = variante;

      const preis = treffer.plan.preis;
      // Stehen die Tage des vorhandenen Plans auf der Karte (strenger Modus), passen sie nicht
      // unbedingt in Monate: dann diese Karte in Tagen
      const dauer = tage % (MONAT * monatSchritt) === 0 ? dauerText(tage) : tageText(tage);
      const rhythmus = dauer === '1 Monat' ? 'jeden Monat' : `alle ${dauer}`;
      // Nicht jede Karte hat jedes Feld (kompakte Karte mit erst_rabatt: kein lieferung, kein pro-tag)
      const setzen = (name, text) => {
        const element = feld(name);
        if (element) element.textContent = text;
        return element;
      };
      setzen('vorrat', `${dauer} ${wort}`);
      setzen('lieferung', `Geliefert ${rhythmus}`);
      const prozent = Math.round((1 - preis / variante.preis) * 100);
      // Abo-Rabatt der Karte in der Häkchenliste (punkte)
      karte.querySelectorAll('[data-db-vorrat-feld="prozent"]').forEach((element) => {
        element.textContent = String(prozent);
      });
      // erst_modus 'punkte': Abo-Rabatt und Willkommens-Rabatt addieren sich auf den Normalpreis (10 + 15 = 25 %);
      // sonst geht der Willkommens-Rabatt vom Abo-Preis ab
      const punkte = vorrat.dataset.erstModus === 'punkte';
      let erstPreis = preis;
      if (erstRabatt && punkte) erstPreis = Math.round((variante.preis * (100 - prozent - erstRabatt)) / 100);
      else if (erstRabatt) erstPreis = Math.round((preis * (100 - erstRabatt)) / 100);
      // Etikett „Spare X %“: aus den angezeigten Preisen gerechnet, damit Zahl und Preis immer zusammenpassen
      const ersparnis = Math.round((1 - erstPreis / variante.preis) * 100);
      setzen('gesamt', geld(vorrat, erstPreis));
      const streich = setzen('streich', geld(vorrat, variante.preis));
      if (streich) streich.hidden = erstPreis >= variante.preis;
      setzen('pro-tag', `${geld(vorrat, Math.round(preis / tage))}/Tag`);
      // preis_pro_tag: der angezeigte Preis der 1. Lieferung je Tag des Vorrats
      setzen('pro-tag-erst', `${geld(vorrat, Math.round(erstPreis / tage))} pro Tag`);
      setzen('danach', `danach ${geld(vorrat, preis)} ${rhythmus}`);
      const pille = setzen('pille', `−${prozent}\u00a0% bei jeder Lieferung`);
      const etikettText = (vorrat.dataset.etikettVorlage || 'Spare [ersparnis]\u00a0%').replace('[ersparnis]', ersparnis);
      const etikettErsparnis = setzen('ersparnis', etikettText);
      if (etikettErsparnis) etikettErsparnis.hidden = ersparnis <= 0;
      if (pille) pille.hidden = prozent <= 0;

      const aktiv = String(variante.id) === gewaehlteId;
      karte.setAttribute('aria-checked', String(aktiv));
      if (aktiv) gewaehlt = { variante, treffer, tage, preis, erstPreis, prozent };
    });

    // Die gewählte Größe gibt es bei diesem Gewicht nicht: auf die erste sichtbare wechseln
    if (!gewaehlt && ersteSichtbare && vorrat.dataset.einmalkauf !== 'laeuft') {
      varianteWaehlen(vorrat, ersteSichtbare.id);
      return;
    }
    if (!gewaehlt) return;

    // Während eines Einmalkaufs bleibt das Feld leer (siehe einmalKaufen)
    const feld = planFeld(sektion);
    if (feld) feld.value = vorrat.dataset.einmalkauf === 'laeuft' ? '' : String(gewaehlt.treffer.plan.id);

    // Preis der gewählten Karte auf dem Kaufbutton. Focal und die Agentur-Animation schreiben den
    // Button-Text selbst neu, ein eingesetztes Element ginge dabei verloren. Deshalb stehen die Werte
    // als CSS-Variablen am Formular, und custom.css hängt sie per ::after an den Button.
    const produktFormular = sektion.querySelector('.product-form');
    if (produktFormular) {
      const reduziert = gewaehlt.erstPreis < gewaehlt.variante.preis;
      produktFormular.dataset.dbKnopf = reduziert ? 'reduziert' : 'voll';
      produktFormular.style.setProperty('--db-knopf-preis', JSON.stringify(geld(vorrat, gewaehlt.erstPreis)));
      produktFormular.style.setProperty('--db-knopf-streich', JSON.stringify(reduziert ? geld(vorrat, gewaehlt.variante.preis) : ''));
    }

    // Fahrplan: [rabatt] ist der dauerhafte Rabatt der gewählten Karte, [danach] der Preis jeder weiteren Lieferung
    vorrat.querySelectorAll('[data-db-vorrat-vorlage]').forEach((element) => {
      const text = element.dataset.dbVorratVorlage
        .replace('[rabatt]', `${gewaehlt.prozent}\u00a0%`)
        .replace('[danach]', geld(vorrat, gewaehlt.preis));
      // ~ ist ein Zeilenumbruch; der Rest bleibt reiner Text
      element.replaceChildren(...text.split('~').flatMap((teil, i) => (i ? [document.createElement('br'), teil] : [teil])));
    });

    // Fehlender Plan in der Vorschau: je Fall einmal in die Konsole, mit hinweis: true auch in den Kasten
    const fehlt = vorschau && !gewaehlt.treffer.genau;
    const meldung = fehlt
      ? `Vorschau: In Recharge fehlt der Plan „alle ${tageText(gewaehlt.tage)}“ für ${gewaehlt.variante.wert}. `
        + `Im Warenkorb landet stattdessen „alle ${tageText(gewaehlt.treffer.plan.tage)}“`
        + (gewaehlt.treffer.rechtzeitig ? '.' : ' – das käme zu spät.')
      : '';
    if (fehlt && !gemeldet.has(meldung)) {
      gemeldet.add(meldung);
      console.info(`[DavyBarkmo] ${meldung}`);
    }
    const hinweis = vorrat.querySelector('[data-db-vorrat-hinweis]');
    if (hinweis) {
      hinweis.hidden = !fehlt;
      hinweis.textContent = meldung;
    }
  };

  const alleAktualisieren = () => document.querySelectorAll(VORRAT).forEach(aktualisieren);

  // Karte gewählt: Focals Variantenwahl umstellen, der Rest folgt über variant:changed
  const varianteWaehlen = (vorrat, variantenId) => {
    const sektion = sektionVon(vorrat);
    const daten = datenVon(vorrat);
    const variante = daten && daten.varianten.find((v) => String(v.id) === String(variantenId));
    if (!variante) return false;
    const radio = [...sektion.querySelectorAll('.product-form__variants input[name^="option"]')].find((r) => r.value === variante.wert);
    if (!radio) return false;
    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    vorrat.querySelectorAll('[data-db-vorrat-karte]').forEach((karte) => {
      karte.setAttribute('aria-checked', String(karte.dataset.dbVorratKarte === String(variantenId)));
    });
    return true;
  };

  // Einmalkauf: kleinste Variante wählen, Plan leeren, über Focals eigenen Kaufbutton absenden
  // (Warenkorb-Schublade und Zähler laufen dann wie gewohnt). Danach gilt wieder das Abo.
  // Stellt Focal die Variante nicht um, geht es ohne Umweg über /cart/add.js in den Warenkorb.
  const einmalKaufen = async (knopf) => {
    const sektion = sektionVon(knopf);
    const vorrat = sektion && sektion.querySelector(VORRAT);
    if (!vorrat || knopf.getAttribute('aria-busy') === 'true') return;
    const variantenId = knopf.dataset.dbVorratEinmalkauf;

    knopf.setAttribute('aria-busy', 'true');
    vorrat.dataset.einmalkauf = 'laeuft';
    try {
      varianteWaehlen(vorrat, variantenId);
      await warte(80);
      aktualisieren(vorrat);

      const formular = formularVon(sektion);
      const kaufknopf = sektion.querySelector('.product-form__buy-buttons [data-product-add-to-cart-button]');
      const stimmt = formular && String(formular.querySelector('input[name="id"]').value) === String(variantenId);
      if (stimmt && kaufknopf && !kaufknopf.disabled) {
        kaufknopf.click();
        await warte(1500);
      } else {
        const antwort = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantenId), quantity: 1 }] }),
        });
        if (!antwort.ok) throw new Error(`cart/add: ${antwort.status}`);
        window.location.href = '/cart';
      }
    } catch (fehler) {
      console.warn('[DavyBarkmo] Einmalkauf fehlgeschlagen.', fehler);
    } finally {
      delete vorrat.dataset.einmalkauf;
      knopf.removeAttribute('aria-busy');
      aktualisieren(vorrat);
    }
  };

  document.addEventListener('click', (event) => {
    const karte = event.target.closest(`${VORRAT} [data-db-vorrat-karte]`);
    if (karte) {
      varianteWaehlen(karte.closest(VORRAT), karte.dataset.dbVorratKarte);
      return;
    }
    const einmal = event.target.closest('[data-db-vorrat-einmalkauf]');
    if (einmal) einmalKaufen(einmal);
  });

  document.addEventListener('db:rechner', alleAktualisieren);
  document.addEventListener('variant:changed', () => setTimeout(alleAktualisieren, 0));
  document.addEventListener('shopify:section:load', alleAktualisieren);

  // Vorwahl (vorwahl: 2): beim Laden die zweite Karte wählen, solange die Adresse keine Variante vorgibt.
  // Focals Variantenwahl hört auf dem Handy erst etwas später zu; deshalb wird das change so lange
  // wiederholt (höchstens 5 s), bis das Kaufformular die Variante wirklich trägt.
  const vorwahlSetzen = () => {
    if (new URLSearchParams(window.location.search).has('variant')) return;
    document.querySelectorAll(`${VORRAT}[data-vorwahl]`).forEach((vorrat) => {
      const karten = [...vorrat.querySelectorAll('[data-db-vorrat-karte]')];
      const karte = karten[Number(vorrat.dataset.vorwahl) - 1];
      if (!karte || karte.hidden) return;
      const ziel = String(karte.dataset.dbVorratKarte);
      const sektion = sektionVon(vorrat);
      const daten = datenVon(vorrat);
      const variante = daten && daten.varianten.find((v) => String(v.id) === ziel);
      if (!sektion || !variante) return;
      let versuche = 0;
      const versuchen = () => {
        const formular = formularVon(sektion);
        const id = formular && formular.querySelector('input[name="id"]');
        if (id && String(id.value) === ziel) {
          aktualisieren(vorrat);
          return;
        }
        const radio = [...sektion.querySelectorAll('.product-form__variants input[name^="option"]')].find((r) => r.value === variante.wert);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        versuche += 1;
        if (versuche < 20) setTimeout(versuchen, 250);
      };
      versuchen();
    });
  };

  alleAktualisieren();
  vorwahlSetzen();
})();

/* ==========================================================================
   DavyBarkmo · Landingpage Duftbaum (templates/product.duftbaum-lp.json) und Kaustab
   (templates/product.kaustab-lp.json)
   Angebotswahl aus snippets/db-lp-probier.liquid: „Probierpaket“ oder „Einzeln“.

   Das Probierpaket ist kein eigenes Produkt: je ein Stück von jeder Variante, den Nachlass gibt der
   automatische Mengenrabatt im Shop. Beim Kauf im Paket-Modus legt das Script die übrigen Varianten
   in den Warenkorb und lässt danach Focals eigenen Kaufvorgang die gewählte Variante hinzufügen —
   Warenkorb-Schublade und Zähler laufen so wie gewohnt und zeigen alle Stücke.
   Abgefangen wird das submit des Kaufformulars in der Aufnahmephase; der Sticky-Button schickt
   dasselbe Formular ab und läuft deshalb mit.
   Mit Gruppen (Kaustab: Größe) besteht das Paket aus den Varianten mit demselben Optionswert wie die
   gewählte Variante. Die Auswahl unter der Paket-Karte stellt Focals eigene (im Paket-Modus
   unsichtbare) Variantenwahl um; Preis, Streichpreis und Ersparnis der Karte folgen.
   ========================================================================== */

(() => {
  const PROBIER = '[data-db-probier]';
  const durchlassen = new WeakSet();

  const sektionVon = (element) => element.closest('.shopify-section');

  const datenVon = (probier) => {
    try {
      return JSON.parse(probier.querySelector('[data-db-probier-daten]').textContent);
    } catch (fehler) {
      return null;
    }
  };

  const modusSetzen = (probier, modus) => {
    probier.dataset.modus = modus;
    probier.querySelectorAll('[data-db-probier-karte]').forEach((karte) => {
      karte.setAttribute('aria-checked', String(karte.dataset.dbProbierKarte === modus));
    });
    const fehler = probier.querySelector('[data-db-probier-fehler]');
    if (fehler) fehler.hidden = true;
  };

  // Gruppen: Karte und Auswahl auf den Optionswert der gewählten Variante stellen
  const gruppeZeigen = (probier, daten, wert) => {
    const gruppe = daten.gruppen && daten.gruppen[wert];
    if (!gruppe) return;
    const feld = (name) => probier.querySelector(`[data-db-probier-feld="${name}"]`);
    if (feld('streich')) feld('streich').textContent = gruppe.summe;
    if (feld('preis')) feld('preis').textContent = gruppe.paket;
    if (feld('prozent')) feld('prozent').textContent = `−${gruppe.prozent} %`;
    probier.querySelectorAll('[data-db-probier-chip]').forEach((chip) => {
      chip.setAttribute('aria-checked', String(chip.dataset.dbProbierChip === wert));
    });
    // Das Paket gibt es in dieser Größe nicht (eine Variante ausverkauft): nur der Einzelkauf bleibt
    if (!gruppe.da && probier.dataset.modus === 'paket') modusSetzen(probier, 'einzeln');
  };

  document.addEventListener('click', (event) => {
    const chip = event.target.closest(`${PROBIER} [data-db-probier-chip]`);
    if (chip) {
      const probier = chip.closest(PROBIER);
      const sektion = sektionVon(probier);
      const daten = datenVon(probier);
      if (!daten || chip.disabled) return;
      const radio = [...sektion.querySelectorAll(`.product-form__variants input[name="option${daten.gruppe_position}"]`)]
        .find((r) => r.value === chip.dataset.dbProbierChip);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (probier.dataset.modus !== 'paket') modusSetzen(probier, 'paket');
      return;
    }
    const karte = event.target.closest(`${PROBIER} [data-db-probier-karte]`);
    if (!karte) return;
    const probier = karte.closest(PROBIER);
    const daten = datenVon(probier);
    // Paket-Karte bei einer Größe ohne Paket: nichts tun, die Auswahl darunter zeigt, was geht
    if (karte.dataset.dbProbierKarte === 'paket' && daten && daten.gruppen) {
      const formular = sektionVon(probier).querySelector('form[is="product-form"]');
      const gewaehlt = formular && daten.varianten.find((v) => String(v.id) === String(formular.querySelector('input[name="id"]').value));
      const gruppe = gewaehlt && daten.gruppen[gewaehlt.gruppe];
      if (gruppe && !gruppe.da) {
        const frei = probier.querySelector('[data-db-probier-chip]:not([disabled])');
        if (frei) frei.click();
        return;
      }
    }
    modusSetzen(probier, karte.dataset.dbProbierKarte);
  });

  // Preis der Einzel-Karte folgt der gewählten Variante, mit Gruppen auch die Paket-Karte
  document.addEventListener('variant:changed', (event) => {
    const sektion = event.target.closest && sektionVon(event.target);
    const probier = sektion && sektion.querySelector(PROBIER);
    const daten = probier && datenVon(probier);
    if (!daten || !event.detail.variant) return;
    const treffer = daten.varianten.find((v) => String(v.id) === String(event.detail.variant.id));
    const feld = probier.querySelector('[data-db-probier-einzelpreis]');
    if (treffer && feld) feld.textContent = treffer.preis;
    if (treffer && daten.gruppen) gruppeZeigen(probier, daten, treffer.gruppe);
  });

  document.addEventListener('submit', async (event) => {
    const formular = event.target;
    if (!formular.matches || !formular.matches('form[is="product-form"]')) return;
    const sektion = sektionVon(formular);
    const probier = sektion && sektion.querySelector(PROBIER);
    if (!probier || probier.dataset.modus !== 'paket') return;
    if (durchlassen.has(formular)) {
      durchlassen.delete(formular);
      return;
    }

    const daten = datenVon(probier);
    const gewaehlt = String(formular.querySelector('input[name="id"]').value);
    if (!daten) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    // Mit Gruppen nur die Varianten mit demselben Optionswert (dieselbe Größe), sonst alle
    const gewaehlteVariante = daten.varianten.find((v) => String(v.id) === gewaehlt);
    const imPaket = daten.gruppen && gewaehlteVariante
      ? daten.varianten.filter((v) => v.gruppe === gewaehlteVariante.gruppe)
      : daten.varianten;
    const uebrige = imPaket.filter((v) => String(v.id) !== gewaehlt).map((v) => ({ id: v.id, quantity: 1 }));
    const knoepfe = [...document.querySelectorAll(`[form="${formular.id}"][type="submit"], #${CSS.escape(formular.id)} [type="submit"]`)];
    knoepfe.forEach((knopf) => knopf.setAttribute('aria-busy', 'true'));
    try {
      const antwort = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: uebrige }),
      });
      if (!antwort.ok) throw new Error(`cart/add: ${antwort.status}`);
      durchlassen.add(formular);
      formular.requestSubmit();
    } catch (fehler) {
      console.warn('[DavyBarkmo] Probierpaket ließ sich nicht in den Warenkorb legen.', fehler);
      const hinweis = probier.querySelector('[data-db-probier-fehler]');
      if (hinweis) hinweis.hidden = false;
    } finally {
      knoepfe.forEach((knopf) => knopf.removeAttribute('aria-busy'));
    }
  }, true);
})();

/* ==========================================================================
   DavyBarkmo · Landingpage Zubehör (templates/product.zubehoer-lp.json)
   Preis aus snippets/db-lp-preis.liquid: Preis, Streichpreis und Rabatt-Pille folgen der gewählten
   Variante. Die Werte je Variante liefert das Snippet mit.
   ========================================================================== */

(() => {
  document.addEventListener('variant:changed', (event) => {
    const sektion = event.target.closest && event.target.closest('.shopify-section');
    const karte = sektion && sektion.querySelector('[data-db-preis]');
    if (!karte || !event.detail.variant) return;
    let daten;
    try {
      daten = JSON.parse(karte.querySelector('[data-db-preis-daten]').textContent)[String(event.detail.variant.id)];
    } catch (fehler) {
      return;
    }
    if (!daten) return;

    const feld = (name) => karte.querySelector(`[data-db-preis-feld="${name}"]`);
    feld('preis').textContent = daten.preis;
    feld('streich').textContent = daten.streich || '';
    feld('streich').hidden = !daten.streich;
    feld('prozent').textContent = `−${daten.prozent} %`;
    feld('prozent').hidden = !(daten.prozent > 0);
  });
})();

/* ==========================================================================
   DavyBarkmo · Warenkorb-Vorschläge (snippets/db-warenkorb-vorschlaege.liquid)
   Tausch der SoulBowl gegen das SoulSet in derselben Farbe und Größe: ein Set dazulegen, eine Bowl
   aus der Zeile nehmen, die Schublade neu aufbauen lassen. Erst das Set, dann die Bowl: Scheitert der
   erste Schritt, ändert sich nichts. Scheitert der zweite, liegen Set und Bowl im Warenkorb, und die
   Schublade zeigt genau das.
   Zählung: Nach einem geglückten Tausch bekommt der Warenkorb das Attribut `_tausch_soulset: ja`. Es
   steht später an der Bestellung (Admin, „Zusätzliche Details“); der Kunde sieht es nicht. Klappt der
   Vermerk nicht, bleibt der Tausch gültig.
   ========================================================================== */

(() => {
  const senden = async (adresse, daten) => {
    const antwort = await fetch(`${adresse}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(daten),
    });
    if (!antwort.ok) throw new Error(`${adresse}: ${antwort.status}`);
    return antwort.json();
  };

  document.addEventListener('click', async (event) => {
    const knopf = event.target.closest && event.target.closest('[data-db-set-tausch-knopf]');
    const tausch = knopf && knopf.closest('[data-db-set-tausch]');
    if (!tausch || knopf.getAttribute('aria-busy') === 'true') return;

    const routen = window.themeVariables.routes;
    const menge = Number(tausch.dataset.menge) || 1;
    const neuAufbauen = (warenkorb) => document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: warenkorb } }),
    );
    let setLiegtDrin = false;
    knopf.setAttribute('aria-busy', 'true');
    try {
      await senden(routen.cartAddUrl, { items: [{ id: Number(tausch.dataset.set), quantity: 1 }] });
      setLiegtDrin = true;
      neuAufbauen(await senden(routen.cartChangeUrl, { id: tausch.dataset.zeile, quantity: menge - 1 }));
      senden(`${routen.cartUrl}/update`, { attributes: { _tausch_soulset: 'ja' } }).catch((fehler) => {
        console.warn('[DavyBarkmo] Tausch gegen das SoulSet nicht vermerkt.', fehler);
      });
    } catch (fehler) {
      console.warn('[DavyBarkmo] Tausch gegen das SoulSet hat nicht geklappt.', fehler);
      if (setLiegtDrin) {
        const antwort = await fetch(`${routen.cartUrl}.js`);
        if (antwort.ok) neuAufbauen(await antwort.json());
        return;
      }
      knopf.removeAttribute('aria-busy');
      const hinweis = tausch.querySelector('[data-db-set-tausch-fehler]');
      if (hinweis) hinweis.hidden = false;
    }
  });
})();
